import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Car, Plus, Trash2, Check, X, ClipboardList, ShieldCheck, ShieldX, CircleAlert as AlertCircle, Phone, MessageCircle, Ban } from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useGamification } from '../contexts/GamificationContext';
import { supabase } from '../lib/supabase';
import { VeiculoChecklistModal } from './VeiculoChecklistModal';
import { useVeiculosFrota, getVeiculoLabel } from '../hooks/useVeiculosFrota';

const empresas = ['Cropfield', 'Solus', 'Cropfert', 'Transcrop', 'Genoma', 'Outra'];

const getVehicleColor = (veiculo: string) => {
  const n = (veiculo || '').trim().toLowerCase();
  if (n.includes('fiat strada')) return '#3B82F6';
  if (n.includes('fiat toro')) return '#10B981';
  if (n.includes('renault kwid') || n.includes('kwid')) return '#F59E0B';
  if (n.includes('renault oroch') || n.includes('oroch')) return '#8B5CF6';
  if (n.includes('saveiro')) return '#EC4899';
  if (n.includes('jeep compass') || n.includes('compass')) return '#EF4444';
  return '#6366F1';
};

const statusConfig: Record<string, { label: string; color: string; bg: string; border: string }> = {
  pending: { label: 'Aguardando Aprovação', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  aguardando_checklist: { label: 'Preencher Checklist', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  approved: { label: 'Aprovado', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
  rejected: { label: 'Reprovado', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
};

interface Reserva {
  id: string;
  nome: string;
  motivo: string;
  empresa: string;
  veiculo: string;
  data_retirada: string;
  horario_retirada: string;
  data_entrega: string;
  horario_entrega: string;
  status: string;
  observacoes_frota?: string;
  aprovado_em?: string;
  usuario_id?: string;
  telefone?: string;
}

interface ApprovalModalState {
  reserva: Reserva | null;
  action: 'approve' | 'reject' | null;
  veiculoSelecionado: string;
}

interface Props {
  checklistId?: string;
}

function buildWhatsAppUrl(phone: string, message: string): string {
  const digits = phone.replace(/\D/g, '');
  const number = digits.startsWith('55') ? digits : `55${digits}`;
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

function isOverdueChecklist(r: Reserva): boolean {
  if (r.status !== 'aguardando_checklist') return false;
  try {
    const deadline = new Date(`${r.data_entrega}T${r.horario_entrega}`);
    deadline.setHours(deadline.getHours() + 1);
    return new Date() > deadline;
  } catch {
    return false;
  }
}

export const VeiculosTab: React.FC<Props> = ({ checklistId }) => {
  const { user } = useAuth();
  const { addActivity } = useGamification();
  const { veiculos: veiculosFrota } = useVeiculosFrota();

  const [reservas, setReservas] = useState<Reserva[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [selectedVeiculo, setSelectedVeiculo] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isBannedFromVehicles, setIsBannedFromVehicles] = useState(false);

  const [veiculoData, setVeiculoData] = useState({
    nome: user?.name || '',
    motivo: '',
    empresa: '',
    dataRetirada: '',
    horarioRetirada: '',
    dataEntrega: '',
    horarioEntrega: '',
    telefone: '',
  });

  const [approvalModal, setApprovalModal] = useState<ApprovalModalState>({ reserva: null, action: null, veiculoSelecionado: '' });
  const [observacoesFrota, setObservacoesFrota] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [checklistReserva, setChecklistReserva] = useState<Reserva | null>(null);
  const [whatsappModal, setWhatsappModal] = useState<{ url: string; nome: string; action: 'approve' | 'reject' } | null>(null);

  const isAdmin = user?.role === 'admin' || user?.setor === 'RH';
  const isFrota = user?.role === 'frota';
  const canApprove = isAdmin || isFrota;

  const checkBanStatus = useCallback(async () => {
    if (!user?.dbId) return;
    const { data } = await supabase
      .from('usuarios')
      .select('banido_veiculos')
      .eq('id', user.dbId)
      .maybeSingle();
    if (data?.banido_veiculos) setIsBannedFromVehicles(true);
  }, [user?.dbId]);

  const loadReservas = useCallback(async () => {
    setDataLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase
        .from('reserva_veiculos')
        .select('*')
        .order('data_retirada', { ascending: false });

      if (error) throw error;

      const list: Reserva[] = data || [];
      setReservas(list);

      const myOverdue = list.filter(
        (r) => String(r.usuario_id) === String(user?.dbId) && isOverdueChecklist(r)
      );
      if (myOverdue.length > 0) {
        setIsBannedFromVehicles(true);
        await supabase
          .from('usuarios')
          .update({ banido_veiculos: true })
          .eq('id', user!.dbId!);
      }

      if (canApprove) {
        const allOverdue = list.filter((r) => r.usuario_id && isOverdueChecklist(r));
        for (const r of allOverdue) {
          await supabase
            .from('usuarios')
            .update({ banido_veiculos: true })
            .eq('id', r.usuario_id!);
        }
      }

      const calendarEvents = list
        .filter((r) => r.status !== 'rejected')
        .map((r) => {
          const baseColor = getVehicleColor(r.veiculo);
          const isPending = r.status === 'pending' || r.status === 'aguardando_checklist';
          const eventTitle = isPending && r.veiculo === 'A definir'
            ? `Aguardando veículo - ${r.nome}`
            : `${r.veiculo} - ${r.nome}${isPending ? ' (Aguardando)' : ''}`;
          return {
            id: r.id,
            title: eventTitle,
            start: `${r.data_retirada}T${r.horario_retirada}`,
            end: `${r.data_entrega}T${r.horario_entrega}`,
            backgroundColor: isPending ? baseColor + '80' : baseColor,
            borderColor: baseColor,
            extendedProps: {
              veiculo: r.veiculo,
              nome: r.nome,
              empresa: r.empresa,
              motivo: r.motivo,
              status: r.status,
            },
          };
        });

      setEvents(calendarEvents);
    } catch (err: any) {
      console.error('[VEICULOS] Load error:', err);
      setLoadError(err?.message || 'Erro ao carregar reservas');
      setReservas([]);
      setEvents([]);
    } finally {
      setDataLoading(false);
    }
  }, [user?.dbId, canApprove]);

  useEffect(() => {
    checkBanStatus();
    loadReservas();
  }, [loadReservas, checkBanStatus]);

  useEffect(() => {
    if (user) setVeiculoData((p) => ({ ...p, nome: user.name }));
  }, [user]);

  useEffect(() => {
    if (!checklistId || reservas.length === 0) return;
    const r = reservas.find((rv) => rv.id === checklistId);
    if (r && r.status === 'aguardando_checklist') {
      setChecklistReserva(r);
    }
  }, [checklistId, reservas]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { nome, motivo, empresa, dataRetirada, horarioRetirada, dataEntrega, horarioEntrega, telefone } = veiculoData;

    if (!nome || !motivo || !empresa || !dataRetirada || !horarioRetirada || !dataEntrega || !horarioEntrega) {
      toast.error('Preencha todos os campos obrigatórios!');
      return;
    }

    if (!telefone || telefone.replace(/\D/g, '').length < 10) {
      toast.error('Informe um número de WhatsApp válido (com DDD).');
      return;
    }

    if (!user?.dbId) {
      toast.error('Erro: usuário não identificado. Faça login novamente.');
      return;
    }

    if (isBannedFromVehicles) {
      toast.error('Sua conta está bloqueada para reservas de veículos. Entre em contato com o analista de frota.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from('reserva_veiculos').insert({
        usuario_id: user.dbId,
        nome,
        motivo,
        empresa,
        veiculo: 'A definir',
        data_retirada: dataRetirada,
        horario_retirada: horarioRetirada + ':00',
        data_entrega: dataEntrega,
        horario_entrega: horarioEntrega + ':00',
        status: 'pending',
        telefone: telefone.replace(/\D/g, ''),
      });

      if (error) throw error;

      addActivity('room_reservation', 'Solicitou reserva de veículo', { tipo: 'veiculo', motivo });
      toast.success('Solicitação enviada! Aguarde a aprovação do analista de frota.');
      setVeiculoData({ nome: user.name || '', motivo: '', empresa: '', dataRetirada: '', horarioRetirada: '', dataEntrega: '', horarioEntrega: '', telefone: '' });
      loadReservas();
    } catch (err: any) {
      toast.error('Erro ao criar solicitação: ' + (err.message || 'Tente novamente'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta reserva?')) return;
    try {
      const { error } = await supabase.from('reserva_veiculos').delete().eq('id', id);
      if (error) throw error;
      toast.success('Reserva excluída.');
      loadReservas();
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('row-level security') || msg.includes('policy')) {
        toast.error('Sem permissão para excluir esta reserva.');
      } else {
        toast.error('Erro ao excluir: ' + (msg || 'Tente novamente'));
      }
    }
  };

  const handleApprovalAction = async () => {
    if (!approvalModal.reserva || !approvalModal.action) return;
    if (approvalModal.action === 'approve' && !approvalModal.veiculoSelecionado) {
      toast.error('Selecione o veículo a ser liberado.');
      return;
    }

    setApprovalLoading(true);
    try {
      const newStatus = approvalModal.action === 'approve' ? 'aguardando_checklist' : 'rejected';
      const token = crypto.randomUUID();
      const updatePayload: Record<string, any> = {
        status: newStatus,
        observacoes_frota: observacoesFrota || null,
        aprovado_por_id: user?.dbId || null,
        aprovado_em: new Date().toISOString(),
      };
      if (approvalModal.action === 'approve') {
        updatePayload.veiculo = approvalModal.veiculoSelecionado;
        updatePayload.checklist_token = token;
      }

      const { error } = await supabase
        .from('reserva_veiculos')
        .update(updatePayload)
        .eq('id', approvalModal.reserva.id);

      if (error) throw error;

      const r = approvalModal.reserva;
      const action = approvalModal.action;
      const veiculoAprovado = approvalModal.veiculoSelecionado;
      const obsTexto = observacoesFrota;

      setApprovalModal({ reserva: null, action: null, veiculoSelecionado: '' });
      setObservacoesFrota('');
      loadReservas();

      if (r.telefone) {
        const checklistLink = `${import.meta.env.VITE_APP_URL || window.location.origin}/checklist/${token}`;
        let message = '';

        if (action === 'approve') {
          message = `Olá ${r.nome}! ✅ Sua reserva de veículo foi *APROVADA*.\n\n🚗 Veículo: *${veiculoAprovado}*\n📅 Retirada: ${r.data_retirada} às ${r.horario_retirada?.slice(0, 5)}\n📅 Devolução: ${r.data_entrega} às ${r.horario_entrega?.slice(0, 5)}\n\n📋 Antes de retirar o veículo, preencha o voucher de saída pelo link abaixo:\n${checklistLink}`;
          if (obsTexto) message += `\n\n📝 Observação: ${obsTexto}`;
        } else {
          message = `Olá ${r.nome}! ❌ Sua solicitação de veículo para ${r.data_retirada} foi *REPROVADA*.`;
          if (obsTexto) message += `\n\n📝 Motivo: ${obsTexto}`;
        }

        setWhatsappModal({ url: buildWhatsAppUrl(r.telefone, message), nome: r.nome, action });
      } else {
        const msg = action === 'approve'
          ? `Reserva aprovada (${veiculoAprovado})! Usuário deverá preencher o checklist.`
          : 'Reserva reprovada.';
        toast.success(msg);
      }
    } catch (err: any) {
      toast.error('Erro ao processar aprovação: ' + (err.message || 'Tente novamente'));
    } finally {
      setApprovalLoading(false);
    }
  };

  const pendingReservas = reservas.filter((r) => r.status === 'pending');
  const myChecklistReservas = reservas.filter(
    (r) => r.status === 'aguardando_checklist' && String(r.usuario_id) === String(user?.dbId)
  );

  const filteredEvents = selectedVeiculo === 'all' ? events : events.filter((e) => e.extendedProps.veiculo === selectedVeiculo);

  const myReservas = reservas;

  return (
    <div className="space-y-6">
      {isBannedFromVehicles && !canApprove && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <Ban className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-red-800">Conta bloqueada para reservas de veículos</h3>
            <p className="text-sm text-red-700 mt-1">
              Você possui um checklist de saída não preenchido após o prazo de devolução. Entre em contato com o analista de frota para regularizar sua situação.
            </p>
          </div>
        </div>
      )}

      {myChecklistReservas.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <ClipboardList className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-800 mb-2">Checklist pendente</h3>
              <p className="text-sm text-blue-700 mb-3">
                Sua(s) reserva(s) abaixo foram aprovadas. Preencha o checklist para confirmar a retirada do veículo.
              </p>
              <div className="space-y-2">
                {myChecklistReservas.map((r) => (
                  <div key={r.id} className="flex items-center justify-between bg-white dark:bg-gray-700 rounded-lg px-4 py-3 border border-blue-200">
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-200">{r.veiculo}</span>
                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">— {r.data_retirada}</span>
                    </div>
                    <button
                      onClick={() => setChecklistReserva(r)}
                      className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <ClipboardList className="w-4 h-4" />
                      Preencher Checklist
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {canApprove && pendingReservas.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h3 className="font-bold text-white">Solicitações Pendentes</h3>
                <p className="text-amber-100 text-sm">{pendingReservas.length} aguardando aprovação</p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {pendingReservas.map((r) => (
              <div key={r.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: getVehicleColor(r.veiculo) + '20' }}
                    >
                      <Car className="w-5 h-5" style={{ color: getVehicleColor(r.veiculo) }} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-800 dark:text-gray-100">{r.veiculo}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        <span className="font-medium">{r.nome}</span> — {r.empresa}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{r.motivo}</div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Retirada: {r.data_retirada} às {r.horario_retirada?.slice(0, 5)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Entrega: {r.data_entrega} às {r.horario_entrega?.slice(0, 5)}
                        </span>
                        {r.telefone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3.5 h-3.5" />
                            {r.telefone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => { setApprovalModal({ reserva: r, action: 'approve', veiculoSelecionado: '' }); setObservacoesFrota(''); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                    >
                      <ShieldCheck className="w-4 h-4" />
                      Aprovar
                    </button>
                    <button
                      onClick={() => { setApprovalModal({ reserva: r, action: 'reject', veiculoSelecionado: '' }); setObservacoesFrota(''); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                    >
                      <ShieldX className="w-4 h-4" />
                      Reprovar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-700">Calendário de Reservas</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Reservas confirmadas aparecem em cor sólida; pendentes em cor transparente</p>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setSelectedVeiculo('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                selectedVeiculo === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Todos
            </button>
            {veiculosFrota.map((v) => {
              const label = getVeiculoLabel(v);
              return (
              <button
                key={v.id}
                onClick={() => setSelectedVeiculo(label)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedVeiculo === label ? 'text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                style={selectedVeiculo === label ? { backgroundColor: v.cor_tag } : undefined}
              >
                {label}
              </button>
              );
            })}
          </div>

          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek' }}
            events={filteredEvents}
            locale="pt-br"
            height="auto"
            contentHeight="auto"
            handleWindowResize
            buttonText={{ today: 'Hoje', month: 'Mês', week: 'Semana' }}
            eventClick={(info) => {
              const p = info.event.extendedProps;
              toast(
                <div className="space-y-1.5">
                  <div className="font-bold text-gray-800">{p.veiculo}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300"><span className="font-medium">Responsável:</span> {p.nome}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300"><span className="font-medium">Empresa:</span> {p.empresa}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300"><span className="font-medium">Motivo:</span> {p.motivo}</div>
                </div>,
                { duration: 4000, style: { minWidth: '260px' } }
              );
            }}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-700">Nova Solicitação de Veículo</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">A solicitação será enviada para aprovação do analista de frota</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {isBannedFromVehicles && !canApprove && (
            <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <Ban className="w-4 h-4 flex-shrink-0" />
              Sua conta está bloqueada para novas reservas. Regularize a situação com o analista de frota.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">Nome *</label>
              <input
                type="text"
                value={veiculoData.nome}
                onChange={(e) => setVeiculoData({ ...veiculoData, nome: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">
                <span className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-green-600" />
                  WhatsApp (com DDD) *
                </span>
              </label>
              <input
                type="tel"
                value={veiculoData.telefone}
                onChange={(e) => setVeiculoData({ ...veiculoData, telefone: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Ex: 44 99999-9999"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">Motivo *</label>
              <input
                type="text"
                value={veiculoData.motivo}
                onChange={(e) => setVeiculoData({ ...veiculoData, motivo: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="Finalidade da viagem"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">Empresa *</label>
              <select
                value={veiculoData.empresa}
                onChange={(e) => setVeiculoData({ ...veiculoData, empresa: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              >
                <option value="">Selecione a empresa</option>
                {empresas.map((e) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
                <Car className="w-4 h-4 flex-shrink-0" />
                O veículo será definido pelo analista de frota no momento da aprovação.
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">Data Retirada *</label>
              <input
                type="date"
                value={veiculoData.dataRetirada}
                onChange={(e) => setVeiculoData({ ...veiculoData, dataRetirada: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">Horário Retirada *</label>
              <input
                type="time"
                value={veiculoData.horarioRetirada}
                onChange={(e) => setVeiculoData({ ...veiculoData, horarioRetirada: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">Data Entrega *</label>
              <input
                type="date"
                value={veiculoData.dataEntrega}
                onChange={(e) => setVeiculoData({ ...veiculoData, dataEntrega: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">Horário Entrega *</label>
              <input
                type="time"
                value={veiculoData.horarioEntrega}
                onChange={(e) => setVeiculoData({ ...veiculoData, horarioEntrega: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                required
              />
            </div>
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button
              type="submit"
              disabled={loading || (isBannedFromVehicles && !canApprove)}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold text-sm disabled:opacity-50 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {loading ? 'Enviando...' : 'Enviar Solicitação'}
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
              A reserva será confirmada após aprovação do analista de frota
            </p>
          </div>
        </form>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-700">
            {canApprove ? 'Todas as Reservas' : 'Minhas Reservas'}
          </h3>
        </div>

        {dataLoading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Carregando reservas...</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-12">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-red-600 text-sm font-medium">Erro ao carregar reservas</p>
            <p className="text-gray-500 text-xs mt-1">{loadError}</p>
            <button onClick={loadReservas} className="mt-3 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Tentar novamente
            </button>
          </div>
        ) : myReservas.length === 0 ? (
          <div className="text-center py-12">
            <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Nenhuma reserva encontrada</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {myReservas.map((r) => {
              const cfg = statusConfig[r.status] || statusConfig.pending;
              const isOwner = String(r.usuario_id) === String(user?.dbId);
              const canChecklist = r.status === 'aguardando_checklist' && isOwner;
              const overdue = isOverdueChecklist(r);

              return (
                <div key={r.id} className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${overdue ? 'bg-red-50' : ''}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: getVehicleColor(r.veiculo) + '20' }}
                      >
                        <Car className="w-5 h-5" style={{ color: getVehicleColor(r.veiculo) }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-gray-800 dark:text-gray-100">{r.veiculo}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">
                          {canApprove
                            ? <span><span className="font-medium">{r.nome}</span> — {r.empresa}</span>
                            : r.empresa
                          }
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">{r.motivo}</div>
                        <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {r.data_retirada} às {r.horario_retirada?.slice(0, 5)}
                          </span>
                          <span>→</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {r.data_entrega} às {r.horario_entrega?.slice(0, 5)}
                          </span>
                          {canApprove && r.telefone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" />
                              {r.telefone}
                            </span>
                          )}
                        </div>
                        {r.observacoes_frota && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">Obs. frota: {r.observacoes_frota}</p>
                        )}
                        {overdue && (
                          <p className="text-xs text-red-600 font-medium mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3.5 h-3.5" />
                            Checklist em atraso — conta bloqueada para novas reservas
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                      {canChecklist && (
                        <button
                          onClick={() => setChecklistReserva(r)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium"
                        >
                          <ClipboardList className="w-3.5 h-3.5" />
                          Checklist
                        </button>
                      )}
                      <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${cfg.color} ${cfg.bg} ${cfg.border}`}>
                        {cfg.label}
                      </span>
                      {(canApprove || (isOwner && r.status === 'pending')) && (
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {approvalModal.reserva && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
            <div
              className={`px-6 py-5 rounded-t-2xl ${
                approvalModal.action === 'approve'
                  ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                  : 'bg-gradient-to-r from-red-500 to-rose-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center">
                    {approvalModal.action === 'approve'
                      ? <ShieldCheck className="w-5 h-5 text-green-600" />
                      : <ShieldX className="w-5 h-5 text-red-600" />
                    }
                  </div>
                  <h3 className="font-bold text-white text-lg">
                    {approvalModal.action === 'approve' ? 'Aprovar Reserva' : 'Reprovar Reserva'}
                  </h3>
                </div>
                <button onClick={() => setApprovalModal({ reserva: null, action: null, veiculoSelecionado: '' })} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1.5 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="font-semibold text-gray-700">{approvalModal.reserva.nome} — {approvalModal.reserva.empresa}</div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">{approvalModal.reserva.motivo}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{approvalModal.reserva.motivo}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {approvalModal.reserva.data_retirada} às {approvalModal.reserva.horario_retirada?.slice(0, 5)} → {approvalModal.reserva.data_entrega} às {approvalModal.reserva.horario_entrega?.slice(0, 5)}
                </div>
                {approvalModal.reserva.telefone && (
                  <div className="text-xs text-green-700 mt-1.5 flex items-center gap-1">
                    <MessageCircle className="w-3.5 h-3.5" />
                    Notificação WhatsApp será enviada para {approvalModal.reserva.telefone}
                  </div>
                )}
              </div>

              {approvalModal.action === 'approve' && (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-2">
                    Veículo a liberar <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {veiculosFrota.map((v) => {
                      const label = getVeiculoLabel(v);
                      const sel = approvalModal.veiculoSelecionado === label;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => setApprovalModal((prev) => ({ ...prev, veiculoSelecionado: label }))}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${sel ? '' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'}`}
                          style={sel ? { borderColor: v.cor_tag, backgroundColor: v.cor_tag + '15' } : undefined}
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: v.cor_tag + '25' }}>
                              <Car className="w-3.5 h-3.5" style={{ color: v.cor_tag }} />
                            </div>
                            <div>
                              <div className="font-semibold text-gray-700 text-xs">{v.modelo}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">{v.placa}</div>
                            </div>
                            {sel && (
                              <div className="ml-auto w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: v.cor_tag }}>
                                <Check className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 dark:text-gray-200 mb-1.5">
                  Observações da Frota {approvalModal.action === 'reject' && <span className="text-red-500">*</span>}
                </label>
                <textarea
                  value={observacoesFrota}
                  onChange={(e) => setObservacoesFrota(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-on-surface focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  rows={3}
                  placeholder={
                    approvalModal.action === 'approve'
                      ? 'Instruções ou observações (opcional)...'
                      : 'Informe o motivo da reprovação...'
                  }
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setApprovalModal({ reserva: null, action: null, veiculoSelecionado: '' })}
                  disabled={approvalLoading}
                  className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors disabled:opacity-50 text-sm"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleApprovalAction}
                  disabled={approvalLoading || (approvalModal.action === 'reject' && !observacoesFrota.trim()) || (approvalModal.action === 'approve' && !approvalModal.veiculoSelecionado)}
                  className={`flex-1 px-4 py-2.5 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 text-sm ${
                    approvalModal.action === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {approvalLoading
                    ? 'Processando...'
                    : approvalModal.action === 'approve' ? 'Confirmar Aprovação' : 'Confirmar Reprovação'
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {whatsappModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm">
            <div className={`px-6 py-5 rounded-t-2xl ${whatsappModal.action === 'approve' ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-rose-600'}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center">
                  <MessageCircle className={`w-5 h-5 ${whatsappModal.action === 'approve' ? 'text-green-600' : 'text-red-600'}`} />
                </div>
                <h3 className="font-bold text-white text-lg">
                  {whatsappModal.action === 'approve' ? 'Reserva Aprovada' : 'Reserva Reprovada'}
                </h3>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 text-sm mb-5">
                Notificar <span className="font-semibold">{whatsappModal.nome}</span> via WhatsApp sobre a decisão da reserva?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setWhatsappModal(null)}
                  className="flex-1 px-4 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors text-sm"
                >
                  Pular
                </button>
                <a
                  href={whatsappModal.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setWhatsappModal(null)}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-colors text-sm flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  Enviar WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {checklistReserva && (
        <VeiculoChecklistModal
          reserva={checklistReserva}
          onClose={() => setChecklistReserva(null)}
          onSuccess={async () => {
            setChecklistReserva(null);
            loadReservas();
          }}
        />
      )}
    </div>
  );
};